import React, { useEffect, useState } from 'react';
import useAxios from 'axios-hooks';
import { PrimaryButton, DefaultButton, Toggle, Checkbox, Spinner } from '@fluentui/react';
import Select from 'react-select';
import { FixedSizeList as List } from 'react-window';
import Toast from '../../../../Forms/Toast';
import FeatureSummary from '../../../../Util/FeatureSummary';
import env from '../../../../../env';

const CustomMenuList = ({ children, ...props }) => {
    const height = 35;
    const itemCount = children.length;
    const totalHeight = Math.min(300, itemCount * height);

    return (
        <List height={totalHeight} itemCount={itemCount} itemSize={height} width="100%">
            {({ index, style }) => (
                <div
                    style={{
                        ...style,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        padding: '0 8px',
                        boxSizing: 'border-box',
                    }}
                    title={children[index]?.props?.children}
                >
                    {children[index]}
                </div>
            )}
        </List>
    );
};

const selectStyles = {
    container: (base) => ({ ...base, width: 500 }),
    control: (base) => ({ ...base, border: '1px solid #aaaaaa' }),
    menuList: (base) => ({ ...base, maxHeight: '300px', overflowY: 'auto' }),
};

const { REACT_APP_OLD_ADMIN_BASE_URL } = env;
const contentEditorUrl = `${REACT_APP_OLD_ADMIN_BASE_URL || ''}Contents/`;

const CustomPoliciesPage = () => {
    const [selectedCobrandId, setSelectedCobrandId] = useState(null);
    const [customEnabled, setCustomEnabled] = useState(false);
    const [rows, setRows] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [toastMsg, setToastMsg] = useState('Saved!');
    const [showError, setShowError] = useState(false);

    const [{ data: cobrands }] = useAxios('/internal/cobrands');
    const [{ data: policyTypes }] = useAxios('/internal/cobrand/policy-content/types');

    const [{ data: settings, loading: loadingSettings }, getSettings] = useAxios(
        { url: `/internal/cobrand/policy-content/settings/${selectedCobrandId}`, method: 'GET' },
        { manual: true },
    );
    const [{ data: conids, loading: loadingConids }, getConids] = useAxios(
        { url: `/internal/cobrand/conids/false/${selectedCobrandId}?includeIncognito=true`, method: 'GET' },
        { manual: true },
    );
    const [, mutate] = useAxios({ method: 'POST' }, { manual: true });

    const cobrandOptions = cobrands && cobrands.map((c) => ({ value: c.cobrandId, label: c.name }));
    const conidOptions = conids && conids.map((c) => ({ value: c.id, label: `${c.id} - ${c.name}` }));

    useEffect(() => {
        if (selectedCobrandId) {
            getSettings();
            getConids();
        }
    }, [selectedCobrandId]);

    useEffect(() => {
        if (!settings) return;
        setCustomEnabled(!!settings.customTermsAndPoliciesOn);
        const nextRows = {};
        (settings.policies || []).forEach((p) => {
            nextRows[p.policyTypeId] = {
                cobrandPolicyContentId: p.cobrandPolicyContentId,
                conId: p.conId,
                isPublished: p.isPublished,
            };
        });
        setRows(nextRows);
    }, [settings]);

    const getRow = (policyTypeId) => rows[policyTypeId] || { cobrandPolicyContentId: 0, conId: null, isPublished: false };

    const setRow = (policyTypeId, patch) => {
        setRows((prev) => ({
            ...prev,
            [policyTypeId]: { ...getRow(policyTypeId), ...patch },
        }));
    };

    const notify = (msg, isError = false) => {
        setShowError(isError);
        if (!isError) {
            setToastMsg(msg);
            setShowToast(true);
        }
    };

    const toggleEnabled = async (enabled) => {
        setCustomEnabled(enabled);
        const result = await mutate({
            url: '/internal/cobrand/policy-content/toggle',
            method: 'POST',
            data: { CoBrandId: selectedCobrandId, Enabled: enabled },
        });
        if (result.isAxiosError) {
            setCustomEnabled(!enabled);
            notify('', true);
        } else {
            notify(enabled ? 'Custom Terms & Policies enabled.' : 'Custom Terms & Policies disabled.');
        }
    };

    const savePolicy = async (policyTypeId) => {
        const row = getRow(policyTypeId);
        const result = await mutate({
            url: '/internal/cobrand/policy-content/update',
            method: 'POST',
            data: {
                CoBrandId: selectedCobrandId,
                PolicyTypeId: policyTypeId,
                ConId: row.conId,
                IsPublished: row.isPublished,
            },
        });
        if (result.isAxiosError) {
            notify('', true);
        } else {
            setRow(policyTypeId, { cobrandPolicyContentId: result.data });
            notify('Policy override saved.');
        }
    };

    const removePolicy = async (policyTypeId) => {
        const row = getRow(policyTypeId);
        if (!row.cobrandPolicyContentId) return;
        const result = await mutate({
            url: `/internal/cobrand/policy-content/delete/${row.cobrandPolicyContentId}`,
            method: 'DELETE',
        });
        if (result.isAxiosError) {
            notify('', true);
        } else {
            setRows((prev) => {
                const next = { ...prev };
                delete next[policyTypeId];
                return next;
            });
            notify('Policy override removed.');
        }
    };

    return (
        <div style={{ maxWidth: 900, marginBottom: 500 }}>
            <h4>Terms &amp; Policies</h4>
            {showError && <div style={{ color: 'rgb(164, 38, 44)' }}>An error has occurred.</div>}

            <div className="mt-1 mb20">
                <label className="default-label mt20">Select Cobrand</label>
                <Select
                    components={{ MenuList: CustomMenuList }}
                    options={cobrandOptions}
                    placeholder="Select Cobrand"
                    value={cobrandOptions?.find((o) => o.value === selectedCobrandId) || null}
                    onChange={(option) => setSelectedCobrandId(option.value)}
                    isSearchable
                    styles={selectStyles}
                />
            </div>

            {selectedCobrandId && (loadingSettings || loadingConids) && (
                <Spinner label="Loading cobrand policy settings..." />
            )}

            {selectedCobrandId && !loadingSettings && !loadingConids && (
                <>
                    <Toggle
                        label="Enable custom Terms & Policies for this cobrand"
                        checked={customEnabled}
                        onText="Enabled"
                        offText="Disabled"
                        onChange={(e, checked) => toggleEnabled(checked)}
                    />

                    <p style={{ fontSize: 13, color: '#555', marginTop: 12, marginBottom: 0 }}>
                        Don't see the content you need? Create or edit the article in the{' '}
                        <a href={contentEditorUrl} target="_blank" rel="noopener noreferrer">
                            Content editor in the old admin
                        </a>
                        {' '}(Contents section), then it will appear in the list below.
                    </p>

                    <div className="mt20">
                        {(policyTypes || []).map((pt) => {
                            const row = getRow(pt.policyTypeId);
                            return (
                                <div
                                    key={pt.policyTypeId}
                                    style={{
                                        border: '1px solid #cdcdcd',
                                        borderRadius: 4,
                                        padding: '12px 16px',
                                        marginBottom: 12,
                                        background: '#f7f7f7',
                                    }}
                                >
                                    <div style={{ fontWeight: 600, marginBottom: 8 }}>{pt.policyName}</div>
                                    <label className="default-label">Content</label>
                                    <Select
                                        components={{ MenuList: CustomMenuList }}
                                        options={conidOptions}
                                        placeholder="Select content"
                                        value={conidOptions?.find((o) => o.value === row.conId) || null}
                                        onChange={(option) => setRow(pt.policyTypeId, { conId: option.value })}
                                        isSearchable
                                        styles={selectStyles}
                                    />
                                    <div style={{ display: 'flex', alignItems: 'center', marginTop: 12, gap: 12 }}>
                                        <Checkbox
                                            label="Published"
                                            checked={row.isPublished}
                                            onChange={(e, checked) => setRow(pt.policyTypeId, { isPublished: checked })}
                                        />
                                        <PrimaryButton
                                            text="Save"
                                            disabled={!row.conId}
                                            onClick={() => savePolicy(pt.policyTypeId)}
                                        />
                                        {row.cobrandPolicyContentId ? (
                                            <DefaultButton text="Remove" onClick={() => removePolicy(pt.policyTypeId)} />
                                        ) : null}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            <Toast show={showToast} message={toastMsg} onClose={() => setShowToast(false)} />
            <FeatureSummary
                label="About Terms & Policies"
                summary="Configure cobrand-specific Terms & Policies. Enable custom policies for a cobrand, then assign a content article to each policy type (Privacy, Terms of Service, Cookies, Accessibility) and publish it. When a policy is enabled and published, the platform serves the cobrand-specific content on the corresponding policy page; otherwise it falls back to the global policy. Notice at Collection is part of the Privacy content. If the article you need isn't in the list, create or edit it in the Content editor in the old admin (Contents section)."
            />
        </div>
    );
};

export default CustomPoliciesPage;
